import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

function EmiDashboard() {
  const [allEmis, setAllEmis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all, due_today, due_this_week, overdue, npa
  const [searchTerm, setSearchTerm] = useState('');
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [selectedReminderType, setSelectedReminderType] = useState('');
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [invoiceResult, setInvoiceResult] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [showSettleModal, setShowSettleModal] = useState(false);
  const [settleTarget, setSettleTarget] = useState(null);
  const [settlementAmount, setSettlementAmount] = useState('0');
  const [settlementNotes, setSettlementNotes] = useState('');
  const [settlementMode, setSettlementMode] = useState('complete'); // complete | recovery
  const [settlingNpa, setSettlingNpa] = useState(false);
  const navigate = useNavigate();

  const NPA_THRESHOLD = -50;
  const isNpaEmi = (emi) => {
    if (emi.status !== 'active') return false;
    if (emi.daysDiff != null && emi.daysDiff < NPA_THRESHOLD) return true;
    return emi.defaulterInfo?.recoveryStatus === 'partial' && (emi.remainingAmount || 0) > 0;
  };

  const getContractRemaining = (emi) => {
    if (emi.pendingAmount != null && !Number.isNaN(Number(emi.pendingAmount))) {
      return Math.max(0, Number(emi.pendingAmount));
    }
    const contractTotal = (emi.emiPlan?.totalAmount || 0)
      + (emi.lateFees || []).reduce((s, f) => s + (f.amount || 0), 0);
    let applied = 0;
    (emi.payments || []).forEach((p) => {
      if (p.status !== 'completed') return;
      const amount = Number(p.amount) || 0;
      const appliedPart = Number(p.appliedToContract) || 0;
      const lateFee = Number(p.lateFeeCollected) || 0;
      const breakdown = appliedPart + lateFee + (Number(p.npaRecoveryApplied) || 0) + (Number(p.npaRecoveryExtra) || 0);
      if (breakdown > 0 && Math.abs(breakdown - amount) < 1) {
        applied += appliedPart;
      } else {
        const remaining = Math.max(0, contractTotal - applied);
        applied += Math.min(amount, remaining);
      }
    });
    return Math.max(0, contractTotal - applied);
  };

  const enrichEmiRow = (emi) => {
    const totalPaid = (emi.payments || []).reduce(
      (sum, payment) => (payment.status === 'completed' ? sum + payment.amount : sum),
      0
    );
    const paidInstallments = emi.paidInstallments !== undefined ? emi.paidInstallments
      : (emi.payments || []).filter((p) =>
        p.status === 'completed'
        && p.paymentMethod !== 'down_payment'
        && !p.notes?.includes('Down payment')
        && !p.notes?.includes('Down payment received')
      ).length;

    let nextDueDate = new Date(emi.startDate);
    nextDueDate.setMonth(nextDueDate.getMonth() + paidInstallments + 1);
    const preferredDate = emi.emiPlan.preferredPaymentDate || 1;
    const lastDayOfMonth = new Date(nextDueDate.getFullYear(), nextDueDate.getMonth() + 1, 0).getDate();
    nextDueDate.setDate(Math.min(preferredDate, lastDayOfMonth));

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    nextDueDate.setHours(0, 0, 0, 0);

    const timeDiff = nextDueDate.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    const gracePeriodDays = emi.emiPlan.gracePeriodDays || 2;
    const lateFeePerDay = emi.emiPlan.lateFeePerDay || 15;
    const gracePeriodEndDate = new Date(nextDueDate);
    gracePeriodEndDate.setDate(nextDueDate.getDate() + gracePeriodDays);

    let lateFee = 0;
    let isWithinGracePeriod = false;

    if (daysDiff < 0) {
      if (today <= gracePeriodEndDate) {
        isWithinGracePeriod = true;
      } else {
        const daysAfterGrace = Math.ceil((today.getTime() - gracePeriodEndDate.getTime()) / (1000 * 3600 * 24));
        lateFee = daysAfterGrace * lateFeePerDay;
      }
    }

    return {
      ...emi,
      totalPaid,
      remainingAmount: getContractRemaining(emi),
      paidInstallments,
      totalInstallments: emi.emiPlan.months,
      nextDueDate,
      daysDiff,
      lateFee,
      isOverdue: daysDiff < 0,
      isDueToday: daysDiff === 0,
      isDueThisWeek: daysDiff >= 0 && daysDiff <= 7,
      isWithinGracePeriod,
      gracePeriodDays,
      lateFeePerDay,
      gracePeriodEndDate,
    };
  };

  const isNpaListEntry = (emi) =>
    emi.daysDiff < NPA_THRESHOLD
    || (emi.defaulterInfo?.recoveryStatus === 'partial' && (emi.remainingAmount || 0) > 0);

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${config.API_URL}/emi?status=active&branchId=PB001&includeDefaulters=1`);
      const activeEmis = response.data.map((emi) => enrichEmiRow(emi));
      setAllEmis(activeEmis);
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || 'Failed to load EMIs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const npaList = useMemo(
    () => allEmis.filter(isNpaListEntry),
    [allEmis]
  );

  const nonNpaEmis = useMemo(
    () => allEmis.filter((emi) => !isNpaListEntry(emi)),
    [allEmis]
  );

  const npaCount = npaList.length;

  const filteredEmis = useMemo(() => {
    let list = nonNpaEmis;
    if (filter === 'npa') list = npaList;
    else if (filter === 'due_today') list = nonNpaEmis.filter((emi) => emi.isDueToday);
    else if (filter === 'due_this_week') list = nonNpaEmis.filter((emi) => emi.isDueThisWeek);
    else if (filter === 'overdue') list = nonNpaEmis.filter((emi) => emi.isOverdue);

    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (emi) => emi.customer.phone.includes(searchTerm) || emi.customer.name.toLowerCase().includes(q)
      );
    }

    return [...list].sort((a, b) => a.daysDiff - b.daysDiff);
  }, [allEmis, nonNpaEmis, npaList, filter, searchTerm]);

  const listForStats = useMemo(() => {
    if (filter === 'npa') return npaList;
    return nonNpaEmis;
  }, [filter, npaList, nonNpaEmis]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  // Get status color and text
  const getStatusInfo = (emi) => {
    if (emi.isOverdue) {
      if (emi.isWithinGracePeriod) {
        return {
          color: 'warning',
          text: `Grace Period (${emi.gracePeriodDays} days)`,
          bgColor: 'bg-warning',
          textColor: 'text-dark'
        };
      } else {
        return {
          color: 'danger',
          text: `${Math.abs(emi.daysDiff)} दिन देर`,
          bgColor: 'bg-danger',
          textColor: 'text-white'
        };
      }
    } else if (emi.isDueToday) {
      return {
        color: 'warning',
        text: 'आज due है',
        bgColor: 'bg-warning',
        textColor: 'text-dark'
      };
    } else if (emi.isDueThisWeek) {
      return {
        color: 'info',
        text: `${emi.daysDiff} दिन बाकी`,
        bgColor: 'bg-info',
        textColor: 'text-white'
      };
    } else {
      return {
        color: 'success',
        text: `${emi.daysDiff} दिन बाकी`,
        bgColor: 'bg-light',
        textColor: 'text-dark'
      };
    }
  };

  // Record EMI payment
  const recordPayment = async (emiId, amount, paymentDate, sendConfirmation = false, opts = {}) => {
    const paymentMethod = opts.paymentMethod || 'cash';
    const notes = opts.notes || 'Monthly EMI payment';
    try {
      const response = await axios.post(`${config.API_URL}/emi/${emiId}/payments`, {
        amount: parseFloat(amount),
        date: paymentDate,
        paymentMethod,
        notes,
        sendConfirmation
      });
      
      if (response.data) {
        const extra = response.data.lateFeeCollected > 0
          ? `\nLate fee profit: ₹${response.data.lateFeeCollected}`
          : '';
        if (sendConfirmation) {
          alert(`Payment recorded successfully! Confirmation message sent.${extra}`);
        } else {
          alert(`Payment recorded successfully!${extra}`);
        }
        fetchDashboardData();
      }
    } catch (err) {
      alert('Error recording payment: ' + (err.response?.data?.message || err.message));
    }
  };

  // Quick payment handler
  const handleQuickPayment = (emi) => {
    const paymentDate = prompt('Payment Date (YYYY-MM-DD):', new Date().toISOString().split('T')[0]);
    if (!paymentDate) return;
    
    const defaultAmount = emi.isOverdue 
      ? (emi.emiPlan.monthlyAmount + emi.lateFee).toFixed(0)
      : emi.emiPlan.monthlyAmount.toFixed(0);

    const amount = prompt(`Payment Amount (Monthly: ₹${emi.emiPlan.monthlyAmount.toFixed(0)})`, defaultAmount);
    if (!amount || parseFloat(amount) <= 0) return;
    
    // Ask for confirmation message
    const sendConfirmation = confirm(`Send confirmation message to ${emi.customer.name}?\n\nMessage will include:\n• Payment confirmation\n• Amount: ₹${amount}\n• Product: ${emi.productInfo?.name || 'Product'}`);
    const npaThreshold = -50;
    const isNpa = emi.daysDiff != null && emi.daysDiff < npaThreshold;
    recordPayment(emi._id, amount, paymentDate, sendConfirmation, {
      paymentMethod: isNpa ? 'recovery' : 'cash',
      notes: isNpa ? 'NPA / recovery EMI collection' : 'Monthly EMI payment'
    });
  };

  // Get summary stats
  const getSummaryStats = () => {
    const currentEmis = listForStats;
    const totalActive = currentEmis.length;
    const overdue = currentEmis.filter(emi => emi.isOverdue).length;
    const dueToday = currentEmis.filter(emi => emi.isDueToday).length;
    const dueThisWeek = currentEmis.filter(emi => emi.isDueThisWeek).length;
    const totalLateFees = currentEmis.reduce((sum, emi) => sum + emi.lateFee, 0);
    
    return { totalActive, overdue, dueToday, dueThisWeek, totalLateFees };
  };

  const stats = getSummaryStats();

  const handleOpenSettleNpa = (emi, mode = 'complete') => {
    setSettleTarget(emi);
    setSettlementMode(mode);
    setSettlementAmount(mode === 'recovery' ? '' : '0');
    setSettlementNotes('');
    setShowSettleModal(true);
  };

  const handleSettleNpa = async () => {
    if (!settleTarget) return;
    const amount = parseFloat(settlementAmount);
    if (Number.isNaN(amount) || amount < 0) {
      alert('Valid amount enter karein');
      return;
    }

    const pending = settleTarget.remainingAmount || 0;

    if (settlementMode === 'recovery') {
      if (amount <= 0) {
        alert('Recovery amount ₹0 se zyada hona chahiye');
        return;
      }
    }

    const applied = Math.min(amount, pending);
    const extraProfit = Math.max(0, amount - pending);
    const loss = Math.max(0, pending - amount);
    const confirmMsg = settlementMode === 'recovery'
      ? `NPA Recovery record karein?\n\nCustomer: ${settleTarget.customer.name}\nRecovery: ₹${amount.toLocaleString('en-IN')}\nBaaki pending: ₹${Math.max(0, pending - applied).toLocaleString('en-IN')}${extraProfit ? `\nLate fee profit: ₹${extraProfit.toLocaleString('en-IN')}` : ''}\n\nEMI active rahegi — jab tak pending clear na ho recovery chalti rahegi.`
      : `Complete Settle karein?\n\nCustomer: ${settleTarget.customer.name}\nPending: ₹${pending.toLocaleString('en-IN')}\nRecovery: ₹${amount.toLocaleString('en-IN')}\nCompany Loss: ₹${loss.toLocaleString('en-IN')}${extraProfit ? `\nExtra profit: ₹${extraProfit.toLocaleString('en-IN')}` : ''}\n\nCase close hoga — iske baad recovery nahi hogi. Customer Risky mark hoga.`;

    if (!window.confirm(confirmMsg)) return;

    setSettlingNpa(true);
    try {
      const response = await axios.post(`${config.API_URL}/emi/${settleTarget._id}/settle-npa`, {
        settlementAmount: amount,
        notes: settlementNotes.trim(),
        mode: settlementMode
      });
      alert(response.data.message || 'NPA settled successfully');
      setShowSettleModal(false);
      setSettleTarget(null);
      await fetchDashboardData();
    } catch (err) {
      alert('Error: ' + (err.response?.data?.message || err.message));
    } finally {
      setSettlingNpa(false);
    }
  };

  const handleSendReminders = async () => {
    if (!selectedReminderType) {
      alert('Please select a reminder type');
      return;
    }
    
    setSendingReminders(true);
    setReminderResult(null);
    
    try {
      const response = await axios.post(`${config.API_URL}/emi/send-reminders`, {
        reminderType: selectedReminderType
      });
      
      setReminderResult(response.data);
      setShowReminderModal(false);
      
      // Refresh the dashboard data
      await fetchDashboardData();
      
    } catch (error) {
      console.error('Error sending reminders:', error);
      setReminderResult({
        success: false,
        message: error.response?.data?.message || 'Failed to send reminders'
      });
    } finally {
      setSendingReminders(false);
    }
  };

  const getReminderTypeLabel = (type) => {
    switch (type) {
      case 'overdue':
        return 'Overdue Customers';
      case 'due_today':
        return 'Due Today Customers';
      case 'due_this_week':
        return 'Due This Week Customers';
      default:
        return type;
    }
  };

  const handleSendInvoice = async (emiId) => {
    setSendingInvoice(true);
    setInvoiceResult(null);
    
    try {
      const response = await axios.post(`${config.API_URL}/invoices/send/${emiId}`);
      
      setInvoiceResult(response.data);
      
      // Auto-hide success message after 3 seconds
      if (response.data.success) {
        setTimeout(() => setInvoiceResult(null), 3000);
      }
      
    } catch (error) {
      console.error('Error sending invoice:', error);
      setInvoiceResult({
        success: false,
        message: error.response?.data?.message || 'Failed to send invoice'
      });
    } finally {
      setSendingInvoice(false);
    }
  };

  const handleDownloadInvoice = async (emiId) => {
    setDownloadingInvoice(true);
    
    try {
      const response = await axios.get(`${config.API_URL}/invoices/download/${emiId}`, {
        responseType: 'blob'
      });
      
      // Create a blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${emiId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Failed to download invoice. Please try again.');
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const checkInvoiceEligibility = async (emiId) => {
    try {
      const response = await axios.get(`${config.API_URL}/invoices/eligible/${emiId}`);
      return response.data.eligible;
    } catch (error) {
      console.error('Error checking invoice eligibility:', error);
      return false;
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">
          <h4 className="alert-heading">Error!</h4>
          <p>{error}</p>
          <button className="btn btn-danger" onClick={fetchDashboardData}>Try Again</button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0 d-flex align-items-center">
          <i className="bi bi-calendar-check me-2 fs-2"></i> EMI Due Dashboard
        </h2>
        <div>
          <button className="btn btn-outline-info me-2" onClick={() => navigate('/notification-history')}>
            <i className="bi bi-bell me-1"></i> View Notification History
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/inventory')}>
            <i className="bi bi-plus-circle me-1"></i> Record New Sale
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card bg-primary text-white">
            <div className="card-body">
              <h5 className="card-title">Total Active EMIs</h5>
              <h3>{stats.totalActive}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-danger text-white">
            <div className="card-body">
              <h5 className="card-title">⚠️ Overdue</h5>
              <h3>{stats.overdue}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-warning text-dark">
            <div className="card-body">
              <h5 className="card-title">📅 Due Today</h5>
              <h3>{stats.dueToday}</h3>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-info text-white">
            <div className="card-body">
              <h5 className="card-title">📆 Due This Week</h5>
              <h3>{stats.dueThisWeek}</h3>
            </div>
          </div>
        </div>
      </div>

      {/* Late Fees Alert */}
      {stats.totalLateFees > 0 && (
        <div className="alert alert-warning mb-4">
          <h5>⚠️ Total Late Fees: ₹{stats.totalLateFees.toLocaleString()}</h5>
          <small>15 रुपए प्रति दिन की दर से late fee बढ़ रही है</small>
        </div>
      )}

      {/* Search Box */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-center">
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search customer by phone or name..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                {searchTerm && (
                  <button 
                    className="btn btn-outline-secondary" 
                    type="button"
                    onClick={clearSearch}
                    title="Clear search"
                  >
                    <i className="bi bi-x"></i>
                  </button>
                )}
              </div>
              <div className="form-text small">
                Type to search customers instantly
              </div>
            </div>
            <div className="col-md-6 text-end">
              {searchTerm && (
                <span className="badge bg-info">
                  {filteredEmis.length} results found
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-8">
              <label className="form-label">Filter by Due Status</label>
              <div className="btn-group w-100" role="group">
                <button
                  type="button"
                  className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setFilter('all')}
                >
                  All ({nonNpaEmis.length})
                </button>
                <button
                  type="button"
                  className={`btn ${filter === 'overdue' ? 'btn-danger' : 'btn-outline-danger'}`}
                  onClick={() => setFilter('overdue')}
                >
                  Overdue ({stats.overdue})
                </button>
                <button
                  type="button"
                  className={`btn ${filter === 'due_today' ? 'btn-warning' : 'btn-outline-warning'}`}
                  onClick={() => setFilter('due_today')}
                >
                  Due Today ({stats.dueToday})
                </button>
                <button
                  type="button"
                  className={`btn ${filter === 'due_this_week' ? 'btn-info' : 'btn-outline-info'}`}
                  onClick={() => setFilter('due_this_week')}
                >
                  Due This Week ({stats.dueThisWeek})
                </button>
                <button
                  type="button"
                  className={`btn ${filter === 'npa' ? 'btn-dark' : 'btn-outline-dark'}`}
                  onClick={() => setFilter('npa')}
                  title="50+ दिन overdue — recovery / NPA"
                >
                  NPA ({npaCount})
                </button>
              </div>
            </div>
            <div className="col-md-4">
              <label className="form-label">Send Reminders</label>
              <button
                className="btn btn-success w-100"
                onClick={() => setShowReminderModal(true)}
              >
                <i className="bi bi-bell me-1"></i> Send Reminder Now
              </button>
            </div>
          </div>
        </div>
      </div>

      {filter === 'npa' && (
        <div className="alert alert-dark mb-4">
          <h5 className="alert-heading mb-2">NPA Recovery Zone (50+ din overdue)</h5>
          <p className="mb-2 small">
            Yahan ke cases company loss maane jaate hain. Automated overdue messages <strong>50 din ke baad band</strong> ho jaate hain.
            <strong> Recovery</strong> = amount collect, EMI active, recovery tab tak jab tak pending clear na ho | <strong>Complete Settle</strong> = case close (recovery band) + customer
            <span className="badge bg-danger ms-1">Risky</span> mark.
          </p>
        </div>
      )}

      {/* EMI List */}
      <div className="card">
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Customer</th>
                  <th>Product</th>
                  <th>Next Due Date</th>
                  <th>Monthly EMI</th>
                  <th>Remaining Amount</th>
                  <th>Progress</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmis.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      <p className="text-muted mb-0">
                        {searchTerm 
                          ? `No customers found matching "${searchTerm}"` 
                          : "No active EMIs match the selected filter."
                        }
                      </p>
                      <p className="text-muted mb-0 small">
                        {searchTerm 
                          ? "Try typing a different name or phone number" 
                          : "Try recording a new EMI sale from the Inventory page."
                        }
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredEmis.map(emi => {
                    const statusInfo = getStatusInfo(emi);
                    const progressPercent = emi.totalInstallments > 0 ? (emi.paidInstallments / emi.totalInstallments) * 100 : 0;
                    
                    return (
                      <tr key={emi._id} className={emi.isOverdue ? 'table-danger' : emi.isDueToday ? 'table-warning' : ''}>
                        <td 
                          onClick={async () => {
                            try {
                              // Find customer by phone number
                              const response = await axios.get(`${config.API_URL}/customers/search/${emi.customer.phone}`);
                              if (response.data && response.data.length > 0) {
                                const customer = response.data[0];
                                navigate(`/customer/${customer._id}`);
                              } else {
                                alert('Customer not found');
                              }
                            } catch (error) {
                              console.error('Error finding customer:', error);
                              alert('Failed to open customer profile');
                            }
                          }}
                          style={{ cursor: 'pointer' }}
                          title="Click to view customer profile & EMI history"
                        >
                          <strong>{emi.customer.name}</strong><br />
                          <small className="text-muted">{emi.customer.phone}</small>
                        </td>
                        <td>
                          <strong>{emi.productInfo?.name || 'Product'}</strong><br />
                          {emi.productInfo?.variant && <small className="text-muted">{emi.productInfo.variant}</small>}
                        </td>
                        <td>
                          <strong>{emi.nextDueDate.toLocaleDateString()}</strong><br />
                          <small className={`badge ${statusInfo.bgColor} ${statusInfo.textColor}`}>
                            {statusInfo.text}
                          </small>
                          {emi.defaulterInfo?.recoveryStatus === 'partial' && (
                            <div><small className="badge bg-success mt-1">Recovery chal rahi</small></div>
                          )}
                        </td>
                        <td>
                          <strong>₹{emi.emiPlan.monthlyAmount.toFixed(0)}</strong>
                          {emi.lateFee > 0 && (
                            <div className="text-danger small">
                              + ₹{emi.lateFee} late fee
                            </div>
                          )}
                        </td>
                        <td>
                          <strong>₹{Math.max(0, emi.remainingAmount).toFixed(2)}</strong><br />
                          <small className="text-muted">
                            Paid: ₹{emi.totalPaid.toLocaleString()}
                          </small>
                          {emi.defaulterInfo?.recoveryAmount > 0 && (
                            <div className="text-success small">
                              Recovery: ₹{emi.defaulterInfo.recoveryAmount.toLocaleString()}
                            </div>
                          )}
                        </td>
                        <td>
                          <div className="progress" style={{height: '20px'}}>
                            <div
                              className="progress-bar"
                              role="progressbar"
                              style={{width: `${progressPercent}%`}}
                              aria-valuenow={progressPercent}
                              aria-valuemin="0"
                              aria-valuemax="100"
                            >
                              {emi.paidInstallments}/{emi.totalInstallments} EMIs
                            </div>
                          </div>
                          <small className="text-muted">{Math.round(progressPercent)}% EMIs paid</small>
                        </td>
                        <td>
                          <span className={`badge bg-${emi.daysDiff < -50 || emi.defaulterInfo?.recoveryStatus === 'partial' ? 'dark' : statusInfo.color}`}>
                            {emi.daysDiff < -50 || emi.defaulterInfo?.recoveryStatus === 'partial' ? 'NPA' : emi.isOverdue ? 'OVERDUE' : emi.isDueToday ? 'DUE TODAY' : 'ACTIVE'}
                          </span>
                        </td>
                        <td>
                          {isNpaEmi(emi) ? (
                            <div className="d-flex gap-1 flex-wrap">
                              <button
                                className="btn btn-xs px-2 py-1 btn-outline-success"
                                style={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                                onClick={() => handleOpenSettleNpa(emi, 'recovery')}
                                title="Kuch amount recover karein — baaki pending rahega"
                              >
                                <i className="bi bi-cash-stack"></i> Recovery
                              </button>
                              <button
                                className="btn btn-xs px-2 py-1 btn-outline-dark"
                                style={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                                onClick={() => handleOpenSettleNpa(emi, 'complete')}
                                title="Case close — write-off + risky customer"
                              >
                                <i className="bi bi-x-octagon"></i> Settle
                              </button>
                            </div>
                          ) : (
                            <div className="btn-group">
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleQuickPayment(emi)}
                                title="Record Payment"
                              >
                                <i className="bi bi-currency-rupee me-1"></i> Pay
                              </button>
                              {emi.status === 'completed' && (
                                <>
                                  <button
                                    className="btn btn-sm btn-success me-1"
                                    onClick={() => handleDownloadInvoice(emi._id)}
                                    disabled={downloadingInvoice}
                                    title="Download Invoice PDF"
                                  >
                                    {downloadingInvoice ? (
                                      <i className="bi bi-hourglass-split"></i>
                                    ) : (
                                      <i className="bi bi-download"></i>
                                    )}
                                  </button>
                                  {emi.customer.phone && (
                                    <button
                                      className="btn btn-sm btn-info"
                                      onClick={() => handleSendInvoice(emi._id)}
                                      disabled={sendingInvoice}
                                      title="Send Invoice via WhatsApp"
                                    >
                                      {sendingInvoice ? (
                                        <i className="bi bi-hourglass-split"></i>
                                      ) : (
                                        <i className="bi bi-whatsapp"></i>
                                      )}
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Reminder Result Alert */}
      {reminderResult && (
        <div className={`alert ${reminderResult.success ? 'alert-success' : 'alert-danger'} mt-3`}>
          <h5>{reminderResult.success ? '✅ Reminders Sent Successfully!' : '❌ Failed to Send Reminders'}</h5>
          <p>{reminderResult.message}</p>
          {reminderResult.summary && (
            <div className="mt-2">
              <strong>Summary:</strong>
              <ul className="mb-0 mt-1">
                <li>Total Targeted: {reminderResult.summary.totalTargeted}</li>
                <li>Successfully Sent: {reminderResult.summary.sent}</li>
                <li>Failed: {reminderResult.summary.failed}</li>
                <li>Type: {getReminderTypeLabel(reminderResult.summary.reminderType)}</li>
              </ul>
            </div>
          )}
          <button 
            className="btn btn-sm btn-outline-secondary mt-2"
            onClick={() => setReminderResult(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Send Reminder Modal */}
      {showReminderModal && (
        <div className="modal fade show" style={{display: 'block'}} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-bell me-2"></i>
                  Send EMI Reminders
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowReminderModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <p className="text-muted mb-3">
                  Select which customers you want to send reminders to:
                </p>
                
                <div className="mb-3">
                  <label className="form-label">Reminder Type:</label>
                  <div className="d-grid gap-2">
                    <button
                      type="button"
                      className={`btn ${selectedReminderType === 'overdue' ? 'btn-danger' : 'btn-outline-danger'}`}
                      onClick={() => setSelectedReminderType('overdue')}
                    >
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      Overdue Customers ({stats.overdue})
                    </button>
                    <button
                      type="button"
                      className={`btn ${selectedReminderType === 'due_today' ? 'btn-warning' : 'btn-outline-warning'}`}
                      onClick={() => setSelectedReminderType('due_today')}
                    >
                      <i className="bi bi-calendar-day me-2"></i>
                      Due Today ({stats.dueToday})
                    </button>
                    <button
                      type="button"
                      className={`btn ${selectedReminderType === 'due_this_week' ? 'btn-info' : 'btn-outline-info'}`}
                      onClick={() => setSelectedReminderType('due_this_week')}
                    >
                      <i className="bi bi-calendar-week me-2"></i>
                      Due This Week ({stats.dueThisWeek})
                    </button>
                  </div>
                </div>
                
                {selectedReminderType && (
                  <div className="alert alert-info">
                    <strong>Selected:</strong> {getReminderTypeLabel(selectedReminderType)}
                    <br />
                    <small>
                      WhatsApp reminders bheje jayenge.
                      {selectedReminderType === 'overdue' && (
                        <> Overdue messages sirf <strong>50 din tak</strong> — uske baad NPA zone me manual settle karein.</>
                      )}
                    </small>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowReminderModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-success"
                  onClick={handleSendReminders}
                  disabled={!selectedReminderType || sendingReminders}
                >
                  {sendingReminders ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-send me-2"></i>
                      Send Reminders
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop */}
      {(showReminderModal || showSettleModal) && (
        <div className="modal-backdrop fade show"></div>
      )}

      {/* NPA Settle Modal */}
      {showSettleModal && settleTarget && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className={`modal-header text-white ${settlementMode === 'recovery' ? 'bg-success' : 'bg-dark'}`}>
                <h5 className="modal-title">
                  <i className={`bi ${settlementMode === 'recovery' ? 'bi-cash-stack' : 'bi-x-octagon'} me-2`}></i>
                  {settlementMode === 'recovery' ? 'NPA Recovery' : 'Complete Settle'} — {settleTarget.customer.name}
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowSettleModal(false)} />
              </div>
              <div className="modal-body">
                <div className="btn-group w-100 mb-3" role="group">
                  <button
                    type="button"
                    className={`btn ${settlementMode === 'recovery' ? 'btn-success' : 'btn-outline-success'}`}
                    onClick={() => { setSettlementMode('recovery'); setSettlementAmount(''); }}
                  >
                    Recovery (baaki pending)
                  </button>
                  <button
                    type="button"
                    className={`btn ${settlementMode === 'complete' ? 'btn-dark' : 'btn-outline-dark'}`}
                    onClick={() => { setSettlementMode('complete'); setSettlementAmount('0'); }}
                  >
                    Complete Settle (case close)
                  </button>
                </div>
                <div className={`alert small ${settlementMode === 'recovery' ? 'alert-success' : 'alert-warning'}`}>
                  {settlementMode === 'recovery'
                    ? 'Recovery record hogi. Extra amount late fee profit mein jayega. EMI active — jab tak pending clear na ho NPA list mein rahega.'
                    : 'Case close hoga — iske baad recovery nahi hogi. Kam amount par loss profit se minus. Customer Risky mark hoga.'}
                </div>
                <table className="table table-sm table-borderless mb-3">
                  <tbody>
                    <tr><th>Product</th><td>{settleTarget.productInfo?.name}</td></tr>
                    <tr><th>Days overdue</th><td className="text-danger">{Math.abs(settleTarget.daysDiff)} days</td></tr>
                    <tr><th>Pending amount</th><td>₹{(settleTarget.remainingAmount || 0).toLocaleString('en-IN')}</td></tr>
                    <tr><th>Paid so far</th><td>₹{(settleTarget.totalPaid || 0).toLocaleString('en-IN')}</td></tr>
                    {settleTarget.defaulterInfo?.recoveryAmount > 0 && (
                      <tr><th>Recovery so far</th><td className="text-success">₹{settleTarget.defaulterInfo.recoveryAmount.toLocaleString('en-IN')}</td></tr>
                    )}
                  </tbody>
                </table>
                <div className="mb-3">
                  <label className="form-label">
                    {settlementMode === 'recovery' ? 'Recovery amount (₹)' : 'Final recovery (₹0 = full write-off)'}
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    min="0"
                    step="1"
                    value={settlementAmount}
                    onChange={(e) => setSettlementAmount(e.target.value)}
                    placeholder={settlementMode === 'recovery' ? 'Kitna recover hua?' : '0 for full loss'}
                  />
                </div>
                {settlementAmount !== '' && !Number.isNaN(parseFloat(settlementAmount)) && (
                  <div className="alert alert-secondary py-2 small mb-3">
                    {settlementMode === 'recovery' ? (
                      <>
                        Baaki pending: ₹{Math.max(0, (settleTarget.remainingAmount || 0) - Math.min(parseFloat(settlementAmount || 0), settleTarget.remainingAmount || 0)).toLocaleString('en-IN')}
                        {parseFloat(settlementAmount || 0) > (settleTarget.remainingAmount || 0) && (
                          <> · Late fee profit: ₹{(parseFloat(settlementAmount || 0) - (settleTarget.remainingAmount || 0)).toLocaleString('en-IN')}</>
                        )}
                      </>
                    ) : (
                      <>
                        Company loss: ₹{Math.max(0, (settleTarget.remainingAmount || 0) - parseFloat(settlementAmount || 0)).toLocaleString('en-IN')}
                        {parseFloat(settlementAmount || 0) > (settleTarget.remainingAmount || 0) && (
                          <> · Extra profit: ₹{(parseFloat(settlementAmount || 0) - (settleTarget.remainingAmount || 0)).toLocaleString('en-IN')}</>
                        )}
                      </>
                    )}
                  </div>
                )}
                <div className="mb-0">
                  <label className="form-label">Notes (optional)</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={settlementNotes}
                    onChange={(e) => setSettlementNotes(e.target.value)}
                    placeholder="e.g. Customer ne kuch amount diya, baaki write-off"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowSettleModal(false)}>Cancel</button>
                <button
                  type="button"
                  className={`btn ${settlementMode === 'recovery' ? 'btn-success' : 'btn-dark'}`}
                  onClick={handleSettleNpa}
                  disabled={settlingNpa}
                >
                  {settlingNpa ? 'Saving...' : (settlementMode === 'recovery' ? 'Record Recovery' : 'Confirm Complete Settle')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Result Alert */}
      {invoiceResult && (
        <div className={`alert ${invoiceResult.success ? 'alert-success' : 'alert-danger'} mt-3`}>
          <h5>{invoiceResult.success ? '📄 Invoice Sent Successfully!' : '❌ Failed to Send Invoice'}</h5>
          <p>{invoiceResult.message}</p>
          {invoiceResult.invoiceNumber && (
            <p><strong>Invoice Number:</strong> {invoiceResult.invoiceNumber}</p>
          )}
        </div>
      )}

    </div>
  );
}

export default EmiDashboard; 